import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { detectLang, t, type Lang } from '../lib/i18n'
import {
  fetchApprovedIssues,
  refreshApprovedIssues,
  filterIssues,
  sortIssues,
  type Issue,
  type IssueKind,
  type IssueTarget,
  type IssueSort,
} from '../lib/github-client'
import { RequestCard } from '../components/request-card'
import { EmptyState } from '../components/empty-state'

type State =
  | { status: 'loading' }
  | { status: 'loaded'; issues: Issue[] }
  | { status: 'error'; message: string }

const KIND_OPTIONS: IssueKind[] = ['all', 'request', 'feedback']
const TARGET_OPTIONS: IssueTarget[] = ['all', 'module', 'course', 'lesson', 'doc']

export function RequestsPage() {
  const [params] = useSearchParams()
  const lang = detectLang(params)
  const [state, setState] = useState<State>({ status: 'loading' })
  const [kind, setKind] = useState<IssueKind>('all')
  const [target, setTarget] = useState<IssueTarget>('all')
  const [sortBy, setSortBy] = useState<IssueSort>('top')
  const inFlightRef = useRef<AbortController | null>(null)

  const load = async (force = false) => {
    // Cancel any in-flight request — protects against rapid refresh races and
    // unmount during fetch (state updates after abort are skipped).
    inFlightRef.current?.abort()
    const controller = new AbortController()
    inFlightRef.current = controller
    setState({ status: 'loading' })
    try {
      const issues = force
        ? await refreshApprovedIssues(controller.signal)
        : await fetchApprovedIssues(false, controller.signal)
      if (controller.signal.aborted) return
      setState({ status: 'loaded', issues })
    } catch (err) {
      if (controller.signal.aborted) return
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'unknown',
      })
    }
  }

  useEffect(() => {
    void load(false)
    return () => {
      inFlightRef.current?.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <section className="mx-auto max-w-3xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-text">
            {t(lang, 'requests.title')}
          </h1>
          <p className="mt-2 text-text-muted">{t(lang, 'requests.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => void load(true)}
          disabled={state.status === 'loading'}
          className="shrink-0 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-text-muted transition-colors hover:border-border-strong hover:text-text disabled:opacity-50"
        >
          ↻ {t(lang, 'requests.refresh')}
        </button>
      </div>

      <FilterBar
        lang={lang}
        kind={kind}
        target={target}
        sortBy={sortBy}
        onKind={setKind}
        onTarget={setTarget}
        onSort={setSortBy}
      />

      <div className="mt-6">
        {state.status === 'loading' && <LoadingSkeleton />}
        {state.status === 'error' && <ErrorBanner lang={lang} onRetry={() => void load(true)} />}
        {state.status === 'loaded' && (
          <IssueList
            lang={lang}
            issues={sortIssues(filterIssues(state.issues, kind, target), sortBy)}
          />
        )}
      </div>
    </section>
  )
}

interface FilterBarProps {
  lang: Lang
  kind: IssueKind
  target: IssueTarget
  sortBy: IssueSort
  onKind: (k: IssueKind) => void
  onTarget: (t: IssueTarget) => void
  onSort: (s: IssueSort) => void
}

function FilterBar({ lang, kind, target, sortBy, onKind, onTarget, onSort }: FilterBarProps) {
  return (
    <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-md border border-border bg-bg-subtle p-3 text-sm">
      <ChipGroup
        items={KIND_OPTIONS.map((k) => ({
          value: k,
          label: t(lang, `requests.filter.kind.${k}` as const),
        }))}
        value={kind}
        onChange={onKind}
      />
      <span className="text-text-subtle">·</span>
      <ChipGroup
        items={TARGET_OPTIONS.map((tg) => ({
          value: tg,
          label: t(lang, `requests.filter.target.${tg}` as const),
        }))}
        value={target}
        onChange={onTarget}
      />
      <select
        value={sortBy}
        onChange={(e) => onSort(e.target.value as IssueSort)}
        className="ml-auto rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs text-text"
      >
        <option value="top">{t(lang, 'requests.sort.top')}</option>
        <option value="new">{t(lang, 'requests.sort.new')}</option>
      </select>
    </div>
  )
}

interface ChipGroupProps<T extends string> {
  items: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
}

function ChipGroup<T extends string>({ items, value, onChange }: ChipGroupProps<T>) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          onClick={() => onChange(it.value)}
          className={
            it.value === value
              ? 'rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground'
              : 'rounded-md px-2 py-0.5 text-xs text-text-muted hover:bg-bg-muted hover:text-text'
          }
        >
          {it.label}
        </button>
      ))}
    </div>
  )
}

function IssueList({ lang, issues }: { lang: Lang; issues: Issue[] }) {
  if (issues.length === 0) return <EmptyState lang={lang} />
  return (
    <ul className="space-y-3">
      {issues.map((issue) => (
        <li key={issue.number}>
          <RequestCard issue={issue} lang={lang} />
        </li>
      ))}
    </ul>
  )
}

function LoadingSkeleton() {
  return (
    <ul className="space-y-3">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="h-32 animate-pulse rounded-lg border border-border bg-bg-muted/50"
        />
      ))}
    </ul>
  )
}

function ErrorBanner({ lang, onRetry }: { lang: Lang; onRetry: () => void }) {
  return (
    <div className="rounded-md border border-danger/30 bg-danger/5 p-4">
      <p className="text-sm text-danger">{t(lang, 'requests.error')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 text-sm font-medium text-danger underline-offset-4 hover:underline"
      >
        Retry
      </button>
    </div>
  )
}
